import React, { FC } from 'react';
import { ComponentRendering, Placeholder, RichText, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import MediaAsset from './components/mediaAsset';
import { SplitModuleModel } from './models/splitModule.model';
import { TSplitModuleProps } from './types/splitModule.types';
import CTAButton, { ctaEnums } from '../@shared/ctaButton';
import { CustomText } from '../@shared/customText';
import RoutableSitecoreLink from '../@shared/routableSitecoreLink';
import Icon from '../@shared/icon';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import { targetLinkdsk } from '../../utils/targetDskLink';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { googleClickCtaEvent, googleClickRichTextEvent } from '../../utils/analytics';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { getQueryLink } from '../../utils/getQuerylink';

const SplitModule: FC<TSplitModuleProps> = (props) => {
  const isEE = props.sitecoreContext?.pageEditing;
  const model = new SplitModuleModel(props);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  model.link1 && getQueryLink(model.link1)
  model.link2 && getQueryLink(model.link2)

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <AnalyticsVisibleEvent rendering={props?.rendering} title={model.title?.value} template="Split Module">
      <div
        className={`split-module ${props.extraClassName} ${model.imagePosition === 'right' && 'split-module--right'}`}
        style={{ backgroundColor: model.backgroundColor || '#f4f4f4' }}
      >
        <div className="split-module__column split-module__media">
          <MediaAsset
            id={model.id}
            carouselSlide={model.carouselSlide}
            slides={model.slides}
            useVideo={model.useVideo}
            image={model.image}
            mobileImage={model.mobileImage}
            video={model.video}
            videoHeading={model.videoHeading}
            videoSubHeading={model.videoSubheading}
            isEE={isEE}
          />
        </div>
        <div className={'split-module__column'}>
          <div className={'split-module__content'}>
            {model.eyebrow && <Text tag="p" field={model.eyebrow} className={'split-module__eyebrow'} />}
            <CustomText tag={model.headingTag} field={model.title} className={'split-module__title'} />
            {model.bodyText && (
              <RichText
                tag="div"
                field={model.bodyText}
                className={'split-module__text rich-text color-primary-link'}
                onClick={(event) => googleClickRichTextEvent(event, model.title?.value ?? model.eyebrow?.value)}
              />
            )}
            <div className={'split-module__buttons'}>
              {(isEE || (model.link1?.value?.text && model.link1?.value?.href)) && (
                <CTAButton type={ctaEnums.primaryRed}>
                  <RoutableSitecoreLink
                    className="split-module__primary-link"
                    field={model.link1}
                    target={targetLinkdsk(model?.link1, isDesktop)}
                    onClick={() => googleClickCtaEvent('Button', model?.link1?.value.text, model.title?.value ?? model.eyebrow?.value)}
                  />
                </CTAButton>
              )}
              {(isEE || (model.link2?.value?.text && model.link2?.value?.href)) && (
                <div className={'split-module__secondary-link'}>
                  <RoutableSitecoreLink
                    field={model.link2}
                    target={targetLinkdsk(model?.link2, isDesktop)}
                    onClick={() => googleClickCtaEvent('Button', model?.link2?.value.text, model.title?.value ?? model.eyebrow?.value)}
                  />
                  <span className="link-with-arrow link-with-arrow--text">
                    <Icon iconName="arrow-simple-right" size={24} color="primary" />
                  </span>
                </div>
              )}
            </div>
            <div className="split-module__pop-up">
              <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
            </div>
          </div>
        </div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(SplitModule);
